// Normalisation et mise en forme des événements EONET.

const ACRES_TO_HECTARES = 0.404686

// Découpage géographique approximatif, suffisant pour agréger et filtrer.
// L'ordre compte : la première boîte qui contient le point l'emporte.
const REGIONS = [
    { id: 'oceania', label: 'Océanie', lng: [110, 180], lat: [-50, 0] },
    { id: 'asia', label: 'Asie', lng: [45, 180], lat: [0, 80] },
    { id: 'europe', label: 'Europe', lng: [-25, 45], lat: [36, 72] },
    { id: 'africa', label: 'Afrique', lng: [-20, 52], lat: [-37, 36] },
    { id: 'north-america', label: 'Amérique du Nord', lng: [-170, -50], lat: [12, 85] },
    { id: 'south-america', label: 'Amérique du Sud', lng: [-95, -30], lat: [-60, 12] },
]

const OTHER_REGION = { id: 'other', label: 'Autre', lng: null, lat: null }

export const REGION_OPTIONS = [...REGIONS, OTHER_REGION].map(({ id, label }) => ({ id, label }))

const findRegion = (lat, lng) =>
    REGIONS.find(r => lng >= r.lng[0] && lng <= r.lng[1] && lat >= r.lat[0] && lat <= r.lat[1]) || OTHER_REGION

// EONET peut renvoyer plusieurs positions successives pour un même événement :
// la dernière est la plus récente.
export const normalizeEvent = raw => {
    const geo = raw.geometry?.[raw.geometry.length - 1]
    if (!geo || geo.type !== 'Point' || !Array.isArray(geo.coordinates)) return null

    const [lng, lat] = geo.coordinates
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null

    const source = raw.sources?.[0]
    const region = findRegion(lat, lng)
    const value = geo.magnitudeValue

    return {
        id: raw.id,
        title: raw.title,
        description: raw.description,
        lat,
        lng,
        date: geo.date,
        timestamp: Date.parse(geo.date),
        // Ramené en hectares pour que les deux sources soient comparables :
        // IRWIN publie en acres, GDACS en hectares.
        hectares: Number.isFinite(value)
            ? (geo.magnitudeUnit === 'acres' ? value * ACRES_TO_HECTARES : value)
            : null,
        sourceId: source?.id ?? 'inconnue',
        sourceUrl: source?.url ?? null,
        regionId: region.id,
        regionLabel: region.label,
        isActive: raw.closed === null,
    }
}

export const formatArea = hectares => {
    if (hectares == null) return 'non renseignée'
    if (hectares >= 1_000_000) return `${(hectares / 1_000_000).toFixed(1)} M ha`
    if (hectares >= 1_000) return `${Math.round(hectares / 1_000)} k ha`
    if (hectares >= 10) return `${Math.round(hectares)} ha`
    return `${hectares.toFixed(1)} ha`
}

const RELATIVE = new Intl.RelativeTimeFormat('fr', { numeric: 'auto' })

export const formatRelative = (timestamp, now = Date.now()) => {
    if (!Number.isFinite(timestamp)) return '—'
    const minutes = Math.round((timestamp - now) / 60_000)
    if (Math.abs(minutes) < 60) return RELATIVE.format(minutes, 'minute')
    const hours = Math.round(minutes / 60)
    if (Math.abs(hours) < 24) return RELATIVE.format(hours, 'hour')
    return RELATIVE.format(Math.round(hours / 24), 'day')
}

const DATE_TIME = new Intl.DateTimeFormat('fr-FR', { dateStyle: 'medium', timeStyle: 'short' })

export const formatDateTime = timestamp =>
    Number.isFinite(timestamp) ? DATE_TIME.format(new Date(timestamp)) : '—'

export const formatCoords = (lat, lng) =>
    `${Math.abs(lat).toFixed(3)}° ${lat >= 0 ? 'N' : 'S'}, ${Math.abs(lng).toFixed(3)}° ${lng >= 0 ? 'E' : 'O'}`

// Trois paliers de fraîcheur : pilotent la couleur des marqueurs et de la légende.
export const freshnessOf = (timestamp, now = Date.now()) => {
    const hours = (now - timestamp) / 3_600_000
    if (hours <= 24) return 'hot'
    if (hours <= 72) return 'warm'
    return 'cold'
}

export const FRESHNESS_LABELS = {
    hot: 'Moins de 24 h',
    warm: '1 à 3 jours',
    cold: 'Plus de 3 jours',
}

export const summarize = events => {
    const regions = new Set()
    let totalHectares = 0
    let withArea = 0
    let latest = null
    let largest = null

    for (const ev of events) {
        regions.add(ev.regionLabel)
        if (ev.hectares != null) {
            totalHectares += ev.hectares
            withArea += 1
            if (!largest || ev.hectares > largest.hectares) largest = ev
        }
        if (!latest || ev.timestamp > latest.timestamp) latest = ev
    }

    return { count: events.length, regions: regions.size, totalHectares, withArea, latest, largest }
}
